import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { State } from '../types/state';
import { ConfigService } from './config.service';
import { DisplayAuthService } from './display-auth.service';
import { NavigationService } from './navigation.service';
import { StorageService } from './storage.service';

@Injectable({
  providedIn: 'root'
})
export class StateService {

  private readonly config = inject(ConfigService);
  private readonly storageService = inject(StorageService);
  private readonly authService = inject(DisplayAuthService);
  private readonly navigationService = inject(NavigationService);

  private readonly stateSubject = new BehaviorSubject<State>(State.IDLE);

  constructor() {
    this.bindStateNavigation();
  }

  public onState(): Observable<State> {
    return this.stateSubject.asObservable();
  }

  public get state(): State {
    return this.stateSubject.value;
  }

  public get currentStateDefaultUrl(): string {
    switch (this.state) {
      case State.ADMINISTRATION:
        return this.navigationService.adminUrl;
      default:
        return this.navigationService.initializationUrl;
    }
  }

  public async initialize(): Promise<void> {
    if (this.state !== State.IDLE) {
      return;
    }
    this.changeState(State.INITIALIZATION);
    if (await this.checkAuthState()) {
      return;
    }
    const transactionId = (this.storageService.data.authState as { transactionId: string; }).transactionId;
    console.debug(`Display not paired, starting pairing mode, transactionId = ${transactionId}`);
    this.changeState(State.PAIRING);
  }

  public async checkAuthState(): Promise<boolean> {
    const isAuthorized = await this.authService.checkAuth();
    if (isAuthorized) {
      console.debug('Display paired, starting signage mode');
      this.changeState(State.SIGNAGE);
    }
    return isAuthorized;
  }

  public setAdministrationState(): void {
    this.changeState(State.ADMINISTRATION);
  }

  public setSignagePreviewState(): void {
    this.changeState(State.SIGNAGE_PREVIEW);
  }

  private bindStateNavigation(): void {
    this.stateSubject
      .subscribe(state => {
        console.log(`State = ${state}`);
        if (this.isWorkingState(this.state)) {
          this.navigateToWorkingStateDefaultUrl(this.state);
        }
      });
  }

  private isWorkingState(state: State): state is State.SIGNAGE | State.PAIRING {
    return state === State.PAIRING || state === State.SIGNAGE;
  }

  private getWorkingStateDefaultUrl(workingState: State.SIGNAGE | State.PAIRING): string {
    switch (workingState) {
      case State.PAIRING:
        return this.navigationService.pairingUrl;
      case State.SIGNAGE:
        return this.navigationService.signageUrl;
    }
  }

  private navigateToWorkingStateDefaultUrl(workingState: State.SIGNAGE | State.PAIRING): void {
    this.navigationService.navigateByUrl(this.getWorkingStateDefaultUrl(workingState));
  }

  private changeState(newState: State): void {
    this.stateSubject.next(newState);
  }

}
